// Single source of truth for "build a link to a user's profile + tell
// the tracker where the click came from". Every surface that navigates
// to /profile/@<username> goes through here so we can later add a new
// source (or drop one) without sweeping every Link/navigate call.
//
// On the profile page, the `from` and `fromId` params are read on mount
// and stripped from the URL with `replace: true` so they don't end up
// in shared/copied links. The values flow into the trackProfileView
// POST body as `source` and `sourceRef`.

export type ProfileViewSource =
  | 'direct'
  | 'feed'
  | 'hashtag'
  | 'marketplace'
  | 'previously_owned'
  | 'discovery'
  | 'top_profiles'
  | 'leaderboard'
  | 'chat'
  | 'messages'
  | 'provenance'
  | 'user_profiles'
  | 'user_activity'
  | 'follow_list'
  | 'achievements'
  | 'analytics'
  | 'spotlight';

interface BuildOpts {
  /** Per-source identifier: for spotlight, the spotlight_promotion.id;
   *  for feed, the post.id; for notification, the notification.id; etc. */
  ref?: string;
}

/**
 * Build a profile link with optional source tracking metadata.
 *
 *   profileLink('alice', 'feed')                 // → /profile/@alice?from=feed
 *   profileLink('@alice', 'spotlight', { ref })  // → /profile/@alice?from=spotlight&fromId=<ref>
 *
 * Pass the username with or without the leading '@' — we normalize.
 */
export function profileLink(
  username: string,
  source: ProfileViewSource,
  opts: BuildOpts = {},
): string {
  const stripped = username.startsWith('@') ? username.substring(1) : username;
  const params = new URLSearchParams();
  // 'direct' is the implicit default — omitting the param keeps URLs
  // clean for the common case (typed URL, refresh).
  if (source !== 'direct') params.set('from', source);
  if (opts.ref) params.set('fromId', opts.ref);
  const qs = params.toString();
  return qs ? `/profile/@${stripped}?${qs}` : `/profile/@${stripped}`;
}
